<template>
  <div class="mt-5 px-6">
    <div
      class="relative h-52 cursor-text rounded-sm border-2 p-2 transition-all"
      :class="{
        'border-error': maxLimitReached,
        'border-main': !maxLimitReached && !isInputFocused,
        'border-primary-solid': !maxLimitReached && isInputFocused,
      }"
      @click="focusOnInput"
    >
      <div class="h-48 overflow-auto">
        <span
          class="absolute -top-3 left-2 z-0 bg-surface px-1 transition-all"
          :class="{
            'text-error': maxLimitReached,
            'text-primary': !maxLimitReached && isInputFocused,
            'text-subtlest': !maxLimitReached && !isInputFocused,
          }"
        >
          {{ props.label }}
        </span>
        <span
          v-for="(num, index) in ticketNumbers"
          :key="num"
          class="mt-2 mr-1 chip inline-flex items-center rounded-full bg-primary-strongest pr-1 text-label-s"
        >
          <span class="mr-1">{{ num }}</span>
          <SvgIcon
            :path="mdiCloseCircle"
            :data-testid="`chip-remove-${index}`"
            class="cursor-pointer"
            :size="20"
            @click="removeTicket(index)"
          />
        </span>
        <input
          v-if="!maxLimitReached"
          ref="chipInput"
          v-model="inputValue"
          data-testid="chip-input"
          type="text"
          class="mt-2 w-[135px] bg-surface outline-none"
          @keyup="addItemToList"
          @keydown="removeItemFromList"
          @focus="isInputFocused = true"
          @blur="isInputFocused = false"
          @paste.prevent="handlePaste"
        />
      </div>
    </div>
    <p v-if="maxLimitReached" data-testid="chip-limit-error" class="mt-3 txt-label-s-regular text-error">
      {{ t('attendee.manualAddModal.limitReachedAlert') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { mdiCloseCircle } from '@mdi/js'
import { nextTick, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  label: string
  maxChips?: number | null
}>()

const inputValue = ref('')
const ticketNumbers = ref<string[]>([])
const isInputFocused = ref(false)
const chipInput = ref()

const emit = defineEmits<{
  (e: 'change', value: string[]): void
}>()

const formattedInputValue = computed<string>(() => {
  if (inputValue.value.includes(',')) return inputValue.value.slice(0, -1)
  return inputValue.value
})

const maxLimitReached = computed(() => {
  if (props.maxChips === null || props.maxChips === undefined) return false
  return ticketNumbers.value.length >= props.maxChips
})

function removeTicket(index: number) {
  ticketNumbers.value.splice(index, 1)
}

function handlePaste(event: ClipboardEvent) {
  if (!event.clipboardData || maxLimitReached.value) return
  let pastedItems = event.clipboardData
    .getData('text')
    .replace(/(?:\r\n|\r|\n| )/g, ',')
    .split(',')
    .filter((item) => item !== '')
  const totalTicketLength = pastedItems.length + ticketNumbers.value.length

  if (props.maxChips && totalTicketLength > props.maxChips) pastedItems = pastedItems.slice(0, -(totalTicketLength - props.maxChips))
  ticketNumbers.value.push(...pastedItems)
}

function addItemToList(keyEvent: KeyboardEvent) {
  if (!formattedInputValue.value.length) return
  if (maxLimitReached.value) return
  if (!['enter', ',', ' '].includes(keyEvent.key.toLowerCase())) return
  if (!formattedInputValue.value.trim().length) return
  if (!ticketNumbers.value.includes(formattedInputValue.value)) ticketNumbers.value.push(formattedInputValue.value)
  inputValue.value = ''
}

function removeItemFromList(keyEvent: KeyboardEvent) {
  if (keyEvent.key.toLowerCase() === 'backspace' && ticketNumbers.value.length && !formattedInputValue.value.length)
    ticketNumbers.value.pop()
}

function focusOnInput() {
  if (maxLimitReached.value) return
  nextTick(() => {
    if (chipInput.value) chipInput.value.focus()
  })
}

watch(ticketNumbers.value, (newValue: string[]) => {
  emit('change', newValue)
})
</script>
